import { AppPromoContentModule } from '@silvertours/common-landingpages-view';
import { analytics, LinkButton } from '@silvertours/front-entities';
import {
  InteractiveText,
  Theme,
  Ui,
  useMobileOS,
} from '@silvertours/front-shared';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import { useMedia } from 'react-use';
import StarRating from './star-rating.png';
import {
  PromoContainer,
  ContentGrid,
  IntroSection,
  Logo,
  Title,
  FeaturesList,
  FeatureItem,
  FeatureDot,
  StoreLinksContainer,
  IllustrationWrapper,
  ContentSection,
  DownloadsContainer,
  Info,
  PictogramContainer,
} from './AppPromoModule.styles';

const AppPromoPictogram = dynamic(() =>
  import('./AppPromoPictogram').then(module => module.AppPromoPictogram),
);

export const AppPromoModule = ({
  badge: { text: badgeText } = { text: '' },
  aboutText = '',
  heading: { text: title } = { text: '' },
  illustrationTextDesktop,
  illustrationTextMobile,
  features: usps = [],
}: AppPromoContentModule) => {
  const t = useTranslations('features.mobileAppPromotion');
  const isMobile = useMedia(
    `(max-width: ${Theme.getBreakpoint('sm')}px)`,
    true,
  );
  const os = useMobileOS();
  const illustrationText = isMobile
    ? illustrationTextMobile
    : illustrationTextDesktop;

  const AppDownloadQRCode = !isMobile
    ? dynamic(
        () => import('./AppDownloadQRCode').then(mod => mod.AppDownloadQRCode),
        {
          ssr: false,
        },
      )
    : () => null;

  return (
    <Ui.Wave type="scalable3" fullWidth backgroundStyle="greenAlt" extendBottom>
      <PromoContainer>
        <ContentGrid>
          <ContentSection>
            <Ui.SectionTitle text={badgeText} type="brand-green" />
            <IntroSection>
              <Logo aria-hidden="true" />
              <Info>
                <Title>{title}</Title>

                <Image
                  src={StarRating}
                  alt=""
                  width="158"
                  style={{ objectFit: 'contain' }}
                />

                <FeaturesList>
                  {usps.map(usp => (
                    <FeatureItem key={usp}>
                      <FeatureDot />
                      {usp}
                    </FeatureItem>
                  ))}
                </FeaturesList>
              </Info>
            </IntroSection>
          </ContentSection>
          <IllustrationWrapper>
            <PictogramContainer>
              <AppPromoPictogram aria-hidden="true" />
            </PictogramContainer>
            <DownloadsContainer>
              {!isMobile && (
                <AppDownloadQRCode
                  aria-label={t('qrCodeLabel')}
                  aria-description={t('qrCodeDescription')}
                />
              )}
              <div>
                <InteractiveText>{illustrationText}</InteractiveText>
                <LinkButton
                  content={{ href: 'app.html', label: aboutText }}
                  variant="secondary"
                  rules={{ expanded: true }}
                />
              </div>
              {isMobile && (
                <StoreLinksContainer>
                  {(os === 'ios' || os === 'unknown') && (
                    <Ui.ResponsiveStoreButton
                      id="app-promo-app-store-anchor"
                      variant="appstore"
                      href={t('appStoreLink')}
                      onClick={analytics.gtm.trackIosAppLinkClicked}
                    />
                  )}
                  {(os === 'android' || os === 'unknown') && (
                    <Ui.ResponsiveStoreButton
                      id="app-promo-play-store-anchor"
                      variant="playstore"
                      href={t('playStoreLink')}
                      onClick={analytics.gtm.trackAndroidAppLinkClicked}
                    />
                  )}
                </StoreLinksContainer>
              )}
            </DownloadsContainer>
          </IllustrationWrapper>
        </ContentGrid>
      </PromoContainer>
    </Ui.Wave>
  );
};
